import PropTypes from "prop-types"
import debounce from "lodash/debounce"
import { useDispatch, useSelector } from "react-redux"
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"

import css from "./LabsSearch.scss"
import { getAssetPath } from "@commonUtils"
import GaEvents from "@containers/Header/GaEvents"
import ImageLoader from "@components/ImageLoader/ImageLoader"
import ListOfHorizontalTiles from "@widgets/ListOfHorizontalTiles/ListOfHorizontalTiles"
import NoResult from "@widgets/NoResult/NoResult"
import LabsSKUHandler from "@flows/LabSKUHandler/LabSKUHandler"

import { trackSearchQuery, trackClearRecentSearches, trackNoTestFound } from "./tracking"
import {
    fetchRecentSearches,
    fetchLabsSearchResults,
    clearRecentSearch,
    fetchLabsSearchSuggestion,
    labsSearchReducer,
} from "./reducer"
import { checkReducerAdded } from "@wrappers/ErrorBoundary/index.js"
import { sendMixpanelTrackEvent } from "@lib/gtmUtility"
import { DIAGNOSTICS } from "@constants"
import { addRequiredReducers } from "@containers/App/reducer"

const searchIcon = getAssetPath("svgs/icons/search_18.svg")
const noResult = getAssetPath("illustrations/no-result-found.svg")

const Dropdown = ({ widgets, recentSearches, clearRecentSearchHandler, query, source, showResults }) => {
    return (
        <div
            className={`${showResults ? css.showSearchDropdown : ""} ${css.searchDropdown}`}
            id="search-dropdown"
        >
            {widgets?.length > 0 ? (
                renderWidgets({
                    widgets,
                    recentSearches: Object.values(recentSearches),
                    clearRecentSearchHandler,
                    query,
                    source,
                })
            ) : (
                <></>
            )}
        </div>
    )
}

const LabsSearch = () => {
    const dispatch = useDispatch()
    useMemo(() => dispatch(addRequiredReducers({ labsSearchReducer })), [dispatch])
    const source = "Labs Page"

    const [query, setQuery] = useState("")
    const [showResults, setShowResults] = useState(false)

    const { searchSuggestions, searchResults, recentSearches } = useSelector((state) =>
        checkReducerAdded(state.labsSearchReducer)
    )
    const city = useSelector((state) => state.locationsReducer.currentCity)
    const widgets = getWidgetsData(query, searchResults, searchSuggestions)

    const dropdDownRef = useRef(null)
    const searchInputRef = useRef(null)
    const handleOutsideClick = (e) => {
        if (!dropdDownRef?.current?.contains(e.target) && !searchInputRef?.current?.contains(e.target)) {
            setShowResults(false)
        }
    }
    useEffect(() => {
        dispatch(fetchRecentSearches())
        window.addEventListener("click", handleOutsideClick)

        return () => {
            window.removeEventListener("click", handleOutsideClick)
        }
    }, [])

    const { itemsInLabsCart, labsItemCategory, pageName } = useSelector((state) => state.shellReducer)

    const selectedSKUIds = useMemo(() => {
        return Object.keys(itemsInLabsCart)
    }, [itemsInLabsCart])

    const debouncedFunction = useCallback(debounce(getSearchResults, 500), [])

    function getSearchResults(q, city) {
        trackSearchQuery(`Search_${q}`)
        dispatch(
            fetchLabsSearchResults({
                q,
                city,
            })
        )
    }

    function clearRecentSearchHandler() {
        trackClearRecentSearches()
        dispatch(clearRecentSearch())
    }

    useEffect(() => {
        if (query) {
            debouncedFunction(query, city)
        }
    }, [query])

    useEffect(() => {
        if (query) {
            return
        }

        dispatch(
            fetchLabsSearchSuggestion({
                city,
                test_ids: selectedSKUIds.length > 0 ? selectedSKUIds : undefined,
                category: labsItemCategory ? labsItemCategory : undefined,
            })
        )
    }, [selectedSKUIds.join(), query])

    useEffect(() => {
        if (showResults && widgets?.length) {
            let sponsoredSkuShown = false
            let isDidYouMean = false
            let isNoResult = false
            const resultCount = widgets.reduce((count, item) => {
                const { data, type } = item
                if (!isDidYouMean && type === "fuzzy_search") isDidYouMean = true
                if (!isNoResult && type === "no_results") isNoResult = true
                if (data) {
                    const hasSponsoredResult = data.results?.some((result) => result?.sponsored)
                    if (!sponsoredSkuShown && hasSponsoredResult) {
                        sponsoredSkuShown = true
                    }
                    return count + Math.min(data.limit || 0, data.results?.length || 0)
                }
                return count
            }, 0)
            if (isNoResult) {
                sendMixpanelTrackEvent("no_search_suggestion_shown", {
                    searched_term: query,
                    page_name: pageName,
                })
            } else
                sendMixpanelTrackEvent("diagnostics_search_suggestions_page_viewed", {
                    business_unit: DIAGNOSTICS,
                    page_name: pageName,
                    no_of_suggestions_shown: resultCount,
                    searched_term: query,
                    sponsored_sku_shown: sponsoredSkuShown,
                    suggestion_type: isDidYouMean ? "did_you_mean" : "general",
                    searched_source: isDidYouMean ? "did_you_mean" : "general",
                })
        }
    }, [showResults, widgets])

    const onInputFocus = () => {
        setShowResults(true)
        GaEvents.onSearchFocus({ page: window.location.pathname })
    }

    return (
        <>
            <div className={`flex alignCenter padding-0-8 ${css.container}`}>
                <input
                    className={css.searchInput}
                    type="text"
                    placeholder="Search for Tests (CBC, MRI, tc.)"
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={onInputFocus}
                    onClick={() => {
                        !query &&
                            sendMixpanelTrackEvent("search_bar_clicked", {
                                business_unit: DIAGNOSTICS,
                                page_name: pageName,
                            })
                    }}
                    ref={searchInputRef}
                />
                <div className={`padding-0-8 ${css.flexShrink0}`}>
                    <ImageLoader src={searchIcon} preload={true} width="18px" height="18px" />
                </div>
                <Dropdown
                    widgets={widgets}
                    recentSearches={recentSearches}
                    clearRecentSearchHandler={clearRecentSearchHandler}
                    query={query}
                    source={source}
                    showResults={showResults}
                    ref={dropdDownRef}
                />
            </div>
            <LabsSKUHandler />
        </>
    )
}

Dropdown.propTypes = {
    query: PropTypes.string,
    source: PropTypes.string,
    widgets: PropTypes.array,
    recentSearches: PropTypes.array,
    clearRecentSearchHandler: PropTypes.func,
    showResults: PropTypes.boolean,
}

function renderWidgets({ widgets, recentSearches, clearRecentSearchHandler, query, source }) {
    return widgets?.map(({ type, data, mixPanelData }, index) => {
        data = data || {}
        switch (type) {
            case "recent_searches": {
                return recentSearches?.length ? (
                    <div className="widget-container padding-0-16">
                        <ListOfHorizontalTiles
                            key={index}
                            type={type}
                            {...data}
                            results={recentSearches}
                            ctaHandler={clearRecentSearchHandler}
                            searchTerm={query}
                            resultType={type}
                            source={source}
                            widgetMixPanel={mixPanelData}
                        />
                    </div>
                ) : null
            }
            case "search":
            case "fuzzy_search":
            case "popular_tests":
            case "popular_packages":
            case "popular_labs":
            case "frequently_booked_tests": {
                return (
                    <div className="widget-container padding-0-16">
                        <ListOfHorizontalTiles
                            key={index}
                            type={type}
                            {...data}
                            searchTerm={query}
                            resultType={type}
                            source={source}
                            widgetMixPanel={mixPanelData}
                        />
                    </div>
                )
            }

            case "no_results": {
                trackNoTestFound(`Search_${query}`)

                return (
                    <div className={`flexColumn widget-container alignCenter`}>
                        <NoResult imageUrl={noResult} header="Sorry, we couldn't find anything" col={6} />
                    </div>
                )
            }

            default:
                return null
        }
    })
}

function getWidgetsData(value, searchResults, searchSuggestions) {
    if (value && searchResults?.length) {
        return searchResults
    } else if (searchSuggestions?.length) {
        return searchSuggestions
    } else {
        return []
    }
}

export default LabsSearch
